import {
  EmployeeAssignTableDataDto,
  CompanyPolicyResponse,
  ResponseErrorDto,
} from '@interfaces/models';
import { PaginationState, SortingState, toast } from '@ui/components';
import { useEmployeesProfile } from '@pages/employees-profile/hooks';
import { useAssignTable } from '@shared/components/assign-table';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, useMemo, useRef } from 'react';
import { updateEmployeeProfile } from '@api/company-policy';
import { getAssignEmployees } from '@api/employees';
import { getTableRequest } from '@shared/utils';
import { useGetEmployees } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { useDebouncedState } from '@ui/hooks';

import { getEmployeesAssignedColumns } from './employees-profile-assign-columns';

interface EmployeesTableState {
  pagination: PaginationState;
  sorting: SortingState;
  globalFilter: string;
}

const employeesTableInitialState: EmployeesTableState = {
  pagination: { pageIndex: 0, pageSize: 10 },
  globalFilter: '',
  sorting: [],
};

export const useEmployeesProfileAssignEmployees = (data?: CompanyPolicyResponse) => {
  const { t } = useTranslation();

  const [assignedEmployees, setAssignedEmployees] = useState<string[]>(
    data?.assignedEmployees || [],
  );
  const [state, setState] = useState<EmployeesTableState>(employeesTableInitialState);
  const [employeeSearch, setEmployeeSearch] = useDebouncedState<string>('', 800);
  const columns = useMemo(
    () => getEmployeesAssignedColumns(t, data?.isDefault ?? false),
    [t, data?.isDefault],
  );
  const totalItemsRef = useRef(0);

  const { employeesOptions, isEmployeesFetching } = useGetEmployees(employeeSearch);
  const { refetchEmployeeProfile } = useEmployeesProfile(data?.id);

  useEffect(() => {
    if (data) setAssignedEmployees(data.assignedEmployees);
  }, [data]);

  const request = useMemo(() => {
    return getTableRequest({
      pagination: state.pagination,
      search: state.globalFilter,
      sorting: state.sorting,
    });
  }, [state]);

  const {
    data: selectedEmployees,
    isLoading: isSelectedEmployeesLoading,
    isFetching: isSelectedEmployeesFetching,
  } = useQuery({
    queryKey: ['employee-profile-employees-table', request, assignedEmployees],
    queryFn: () => getAssignEmployees({ ...request, ids: assignedEmployees }),
  });

  const { mutate } = useMutation({
    mutationFn: (users: string[]) =>
      updateEmployeeProfile(data!.id, { ...data!, assignedEmployees: users }),
    onSuccess: () => {
      toast.success(t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.ASSIGN_SUCCESS'));
      refetchEmployeeProfile();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const setAssignedUsers = (users: string[]) => {
    setAssignedEmployees(users);
    mutate(users);
  };

  const totalItems = useMemo(() => {
    if (!isSelectedEmployeesFetching) {
      totalItemsRef.current = selectedEmployees?.pagination.totalItems ?? 0;
    }
    return totalItemsRef.current;
  }, [selectedEmployees, isSelectedEmployeesFetching]);

  const assignTable = useAssignTable<EmployeeAssignTableDataDto>({
    isLoading: (isSelectedEmployeesLoading && isSelectedEmployeesFetching) || !data,
    onStateChange: (state) => setState(state as EmployeesTableState),
    isOptionLoading: isEmployeesFetching,
    data: selectedEmployees?.data ?? [],
    phraseChange: setEmployeeSearch,
    getRowId: (row) => row.id,
    options: employeesOptions,
    initialState: state,
    totalItems,
    columns,
  });

  return { assignTable, setAssignedUsers, assignedUsers: assignedEmployees, totalItems };
};
